import { useState, useEffect, useMemo } from 'react';
import {
  Box, Button, Paper, Stack, Typography, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ApexPieChart from '../../charts/pie-charts/dashboard';
import { DataColumns } from './constants';
 
import {
  Wrapper,
  SelectDropdown,
  CustomDivider,
  CustomTable,
  TableButton,
  Calender,
} from '../../components';
import { DashboardContent } from '../../components/dashboard';
import {
  Logs,
  apiCategortList,
  dashboardCharts,
  downloadLogs,
  usageLogs,
  dashboardSummary,
  dashboardCredits,
  dashboardRecentActiviy,
} from '../../services';
import { getAllApisWithSubscription } from '../../services/apiCatalogService';
import DateTimeGraph from '../../charts/line-charts/dashboard';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);
 
export const groupSimilarData = (arr = []) => {
  const apiCategory = arr.map(
    (item) => item?.attributes?.apiCategory?.data?.attributes?.name,
  );
 
  const apiCategoryObj = {};
  apiCategory.forEach((element) => {
    apiCategoryObj[element] = [];
  });
 
  arr.forEach((item) => apiCategoryObj[item?.attributes?.apiCategory?.data?.attributes?.name].push({
    name: item?.attributes?.name,
    id: item?.id,
  }));
  return apiCategoryObj;
};
 
export function Dashboard() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md')); // change here
  const { user, credits } = useSelector((state) => state.auth);
  const [dashboardSummaryData, setDashboardSummaryData] = useState(null);
  const [dashboardCreditData, setDashboardCreditData] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const jwtToken = user?.jwt;
  const navigate = useNavigate();
  
   // derive credits from API data
   const totalCredits = dashboardCreditData?.totalAssignedCredits || 0;
   const usedCredits =
     (dashboardCreditData?.totalAssignedCredits || 0) -
     (dashboardCreditData?.totalRemainingCredits || 0);
   const remainingCredits = dashboardCreditData?.totalRemainingCredits || 0;
   const testingCredits = Math.floor(usedCredits * 0.4);
   const liveCredits = usedCredits - testingCredits;
 
  const [dashboardChartsData, setDashboardChartsData] = useState({});
  const [dashboardUsageData, setDashboardUsageData] = useState([]);
  const [apiCategoryAndList, setApiCategoryAndList] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedAPI, setSelectedAPI] = useState('');
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isCalendarChanged, setIsCalendarChanged] = useState(false); // New state
  const [totalApis, setTotalApis] = useState(0);
 
  const currentDate = dayjs();
  const sevenDaysBack = currentDate.subtract(7, 'day');
 
  const defaultDateRange = [sevenDaysBack.startOf('day'), currentDate.endOf('day')];
  const [dateRangeValue, setDateRangeValue] = useState(defaultDateRange);
 
  const [dashboardDates, setDashboardDates] = useState({
    startDate: defaultDateRange[0].format('YYYY-MM-DD'),
    endDate: defaultDateRange[1].format('YYYY-MM-DD'),
  });
 
    // 📊 Fetch summary data
    useEffect(() => {
      const fetchSummary = async () => {
        try {
          const res = await dashboardSummary(jwtToken);
          if (res?.status === 200) {
            setDashboardSummaryData(res.data);
          }
        } catch (error) {
          console.error("Error fetching dashboard summary:", error);
        }
      };
      if (jwtToken) fetchSummary();
    }, [jwtToken]);
 
      // 📊 Fetch dashboard credit data
      useEffect(() => {
        const fetchDashboardCredits = async () => {
          try {
            const res = await dashboardCredits(jwtToken);
            if (res?.status === 200) {
              setDashboardCreditData(res.data);
            }
          } catch (error) {
            Logs(error);
          }
        };
        if (jwtToken) fetchDashboardCredits();
      }, [jwtToken]);
 
// 📊 Fetch recent activity data
useEffect(() => {
  const fetchDashboardRecentActiviy = async () => {
    try {
      const res = await dashboardRecentActiviy(jwtToken);
 
      if (res?.status === 200) {
        const activities = (res?.data?.data || []).slice(0, 3);
        const formatted = activities.map((item) => {
          let status = '';
          let statusColor = '#64748b';
          let title = '';
 
          if (item.type === 'api_call') {
            status = item.metadata?.statusCode === 200 ? 'Success' : 'Failed';
            statusColor = item.metadata?.statusCode === 200 ? '#10b981' : '#ef4444';
            title = 'API Call';
          } else if (item.type === 'credit_purchase') {
            status = 'Completed';
            statusColor = '#10b981';
            title = 'Credit Purchase';
          } else if (item.type === 'subscription') {
            status = 'Active';
            statusColor = '#6366f1';
            title = 'Subscription';
          } else if (item.type === 'support_mail') {
            status = 'Sent';
            statusColor = '#3b82f6';
            title = 'Support Mail';
          } else if (item.type === 'key_generation') {
            status = 'Generated';
            statusColor = '#8b5cf6';
            title = 'Key Generation';
          } else {
            title = 'Activity';
          }
 
          return {
            type: item.type,
            title,
            description: item.description,
            time: dayjs(item.timestamp).fromNow(),
            status,
            statusColor,
          };
        });
 
        setRecentActivities(formatted);
      }
    } catch (error) {
      console.error("Error fetching recent activities:", error);
    }
  };
 
  if (jwtToken) fetchDashboardRecentActiviy();
}, [jwtToken]);
 
 
  useEffect(() => {
    const getApiCategoryList = async () => {
      try {
        const res = await getAllApisWithSubscription();
        if (res?.status === 200) {
          const responseData = res?.data?.data || {};
          const attributes = responseData?.attributes || {};
          const categories = attributes?.apisByCategory || {};
          console.log('Dashboard - API Response:', res.data);
          console.log('Dashboard - totalSubscribedApis:', responseData?.totalSubscribedApis);
          setApiCategoryAndList(categories);
          const subscribedCount = responseData?.totalSubscribedApis || 0;
          console.log('Dashboard - Setting totalApis to:', subscribedCount);
          setTotalApis(subscribedCount);
          setSelectedCategory("all");
        }
      } catch (error) {
        Logs(error);
      }
    };
 
    getApiCategoryList();
  }, []);
 
  useEffect(() => {
    const getDashboardCharts = async () => {
      try {
        const res = await dashboardCharts(selectedCategory === 'all' ? null : selectedAPI, dashboardDates);
        if (res?.status === 200) {
          setDashboardChartsData(res?.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard charts data:', error);
      }
    };
 
    if (selectedCategory) {
      getDashboardCharts();
    }
  }, [selectedAPI, dashboardDates]);
 
  useEffect(() => {
    if (selectedAPI || selectedCategory === 'all') {
      const fetchUsageLogs = async () => {
        try {
          const res = await usageLogs({
            token: jwtToken,
            startDate: dashboardDates?.startDate,
            endDate: dashboardDates?.endDate,
            apiId: selectedCategory === 'all' ? null : selectedAPI,
          });
          if (res?.status === 200) {
            setDashboardUsageData(res?.data);
          }
        } catch (error) {
          console.error('Error fetching usage logs:', error);
        }
      };
      fetchUsageLogs();
    }
  }, [selectedAPI, dashboardDates, jwtToken]);
 
  useEffect(() => {
    setIsFilterActive(selectedCategory !== 'all' || isCalendarChanged);
    if (selectedCategory !== 'all' && apiCategoryAndList[selectedCategory]?.apis) {
      const sortedApis = apiCategoryAndList[selectedCategory]?.apis.sort((a, b) => a.name.localeCompare(b.name));
      setSelectedAPI(sortedApis[0]?.id || '');
    } else {
      setSelectedAPI('');
    }
  }, [selectedCategory, apiCategoryAndList, isCalendarChanged]);
 
  const { apiOptions } = useMemo(() => {
    const selected = apiCategoryAndList[selectedCategory] || [];
    if (selected?.apis?.length > 0) {
      const sortedApiOptions = selected?.apis.map((item) => ({
        label: item.name,
        value: item.id,
      })).sort((a, b) => a.label.localeCompare(b.label));
 
      const selectedApi = selected.apis.find((x) => x.id === selectedAPI);
      return { apiOptions: sortedApiOptions, api: selectedApi };
    }
    return { apiOptions: [], api: null };
  }, [selectedCategory, apiCategoryAndList, selectedAPI]);
 
  const handleChangeCalender = (dates) => {
    if (dates) {
      setDashboardDates({
        startDate: dates[0].format('YYYY-MM-DD'),
        endDate: dates[1].format('YYYY-MM-DD'),
      });
      setDateRangeValue(dates);
      setIsCalendarChanged(true);
    } else {
      setDashboardDates({
        startDate: defaultDateRange[0].format('YYYY-MM-DD'),
        endDate: defaultDateRange[1].format('YYYY-MM-DD'),
      });
      setDateRangeValue(defaultDateRange);
      setIsCalendarChanged(true);
    }
  };
 
  const handleClick = async (id) => {
    try {
      const response = await downloadLogs(selectedCategory === 'all' ? null : id, dashboardDates);
      if (response?.data) {
        const url = URL.createObjectURL(response?.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'logs.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error(error);
    }
  };
 
  const handleResetFilter = () => {
    setSelectedCategory('all');
    setSelectedAPI('');
    setDashboardDates({
      startDate: defaultDateRange[0].format('YYYY-MM-DD'),
      endDate: defaultDateRange[1].format('YYYY-MM-DD'),
    });
    setDateRangeValue(defaultDateRange);
    setIsCalendarChanged(false);
  };
 
  return (
    <Wrapper>
      <Stack sx={{ pt: 2, mt: 9 }} spacing={4} className="wrapper">
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: "#65758B",
                fontSize: "14px",
              }}
            >
              <Typography variant="h4" fontWeight="bold" color="black">
              Welcome back, {user?.username}!
            </Typography>
            <Typography color="black" fontWeight="regular" sx={{ mt: 0.5, opacity: 0.8 }}>
              Here’s what’s happening with your APIs today.
            </Typography>
            </Typography>
          </Box>
        
        <DashboardContent
          totalCredits={totalCredits}
          usedCredits={usedCredits}
          remainingCredits={remainingCredits}
          testingCredits={testingCredits}
          liveCredits={liveCredits}
          monthlyRequests={dashboardSummaryData?.currentMonth?.totalRequests || 0}
          successRateChange={dashboardSummaryData?.successRateChangePercentage || 0}
          totalRequestsChangePercentage={dashboardSummaryData?.totalRequestsChangePercentage || 0}
          currentMonthSuccessRate={dashboardSummaryData?.currentMonth?.successRate || 0}
          totalApis={totalApis}
          recentActivities={recentActivities}
          onNavigate={(path) => navigate(path)}
          onViewAllActivity={() => navigate('/detailed-logs', { state: { showRecentActivities: true } })}
          usagePercentage={dashboardCreditData?.usagePercentage || 0}
        />
        
      </Stack>
    </Wrapper>
  );
}